import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import { 
    FaBell, FaCheckDouble, FaBriefcase, FaMoneyBillWave, 
    FaInfoCircle, FaClock, FaCheckCircle, FaTrashAlt 
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';

const NotificationsDropdown = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const { user } = useAuth();
    const { socket } = useSocket();
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    const fetchNotifications = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const response = await apiClient.get('/notifications');
            setNotifications(response.data);
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchNotifications();
    }, [user, fetchNotifications]);

    useEffect(() => {
        if (socket) {
            socket.on('new_notification', (notif) => {
                setNotifications(prev => [notif, ...prev]);
            });
        }
        return () => socket?.off('new_notification');
    }, [socket]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownRef]);

    const handleMarkAsRead = async (id) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, status: 'read' } : n));
        try {
            await apiClient.post(`/notifications/${id}/read`);
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
        }
    };

    const handleMarkAllRead = async () => {
        const unreadIds = notifications.filter(n => n.status === 'unread').map(n => n.id);
        if (unreadIds.length === 0) return;

        setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
        try {
            await Promise.all(unreadIds.map(id => apiClient.post(`/notifications/${id}/read`)));
        } catch (err) {
            console.error("Failed to mark all as read");
        }
    };

    const handleNotificationClick = (n) => {
        handleMarkAsRead(n.id);
        setIsOpen(false);

        if (n.link) {
            navigate(n.link);
            return;
        }

        const type = n.type?.toUpperCase();
        const id = n.relatedEntityId;

        // --- STRICT ROLE-BASED REDIRECTION ---
        if (user?.role === 'worker') {
            // Workers almost always go to their running jobs for any work update
            if (['JOB_OFFER', 'DIRECT_HIRE', 'BID_ACCEPTED', 'JOB_STARTED', 'HIRE_STATUS_UPDATE', 'WORKER_ARRIVED', 'ROSTER_INVITE'].includes(type)) {
                navigate('/worker-dashboard/running-jobs');
                return;
            }
        }

        switch (type) {
            case 'JOB_COMPLETED':
            case 'WALLET_RECHARGE':
            case 'WITHDRAWAL_REQUEST':
                navigate('/wallet');
                break;

            case 'JOB_APPLICATION':
            case 'NEW_JOB_OPPORTUNITY':
                if (id) navigate(`/job/${id}`);
                break;
            
            case 'JOB_START_REQUEST':
            case 'JOB_COMPLETION_REQUEST':
            case 'WORKER_ARRIVING':
            case 'WORKER_ARRIVED':
            case 'HIRE_STATUS_UPDATE':
                if (id) navigate(`/manage-job/${id}`);
                break;

            default:
                // Default fallback
                if (user?.role === 'buyer') navigate('/owner-dashboard');
                else navigate('/worker-dashboard');
                break;
        }
    };

    const getIcon = (type) => {
        const t = type?.toUpperCase();
        if (['JOB_APPLICATION', 'JOB_OFFER', 'BID_ACCEPTED', 'DIRECT_HIRE'].includes(t)) {
            return <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><FaBriefcase size={16} /></div>;
        }
        if (['WALLET_RECHARGE', 'WITHDRAWAL_REQUEST', 'JOB_COMPLETED'].includes(t)) {
            return <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><FaMoneyBillWave size={16} /></div>;
        }
        return <div className="w-10 h-10 rounded-xl bg-slate-50 text-slate-500 flex items-center justify-center"><FaInfoCircle size={16} /></div>;
    };
    
    const unreadCount = notifications.filter(n => n.status === 'unread').length;

    return (
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={() => setIsOpen(!isOpen)} 
                className={`relative p-2.5 rounded-xl transition-all ${isOpen ? 'bg-emerald-50 text-emerald-600' : 'text-slate-500 hover:bg-slate-50'}`}
            >
                <FaBell size={22} />
                {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white border-2 border-white shadow-sm animate-bounce">
                        {unreadCount}
                    </span>
                )}
            </button>
            
            {isOpen && (
                <div className="absolute right-0 mt-4 w-[380px] bg-white rounded-[2rem] shadow-2xl border border-slate-100 z-50 overflow-hidden animate-fade-in">
                    
                    <div className="px-6 py-5 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
                        <div>
                            <h3 className="text-base font-black text-slate-800">Notifications</h3>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{unreadCount} Unread Alerts</p>
                        </div>
                        {unreadCount > 0 && (
                            <button 
                                onClick={handleMarkAllRead}
                                className="text-[10px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-1.5 hover:text-emerald-700 transition-colors"
                            >
                                <FaCheckDouble /> Mark all read
                            </button>
                        )}
                    </div>

                    <ul className="divide-y divide-slate-50 max-h-[450px] overflow-y-auto custom-scrollbar">
                        {loading && notifications.length === 0 ? (
                            <div className="p-12 text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                            </div>
                        ) : notifications.length > 0 ? notifications.map(n => (
                            <li 
                                key={n.id} 
                                className={`px-6 py-5 hover:bg-slate-50 transition-all cursor-pointer relative group ${n.status === 'unread' ? 'bg-emerald-50/30' : ''}`}
                                onClick={() => handleNotificationClick(n)}
                            >
                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        {getIcon(n.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm leading-snug mb-1 ${n.status === 'unread' ? 'text-slate-900 font-bold' : 'text-slate-600 font-medium'}`}>
                                            {n.message}
                                        </p>
                                        <div className="flex items-center gap-3">
                                            <span className="text-[10px] text-slate-400 font-bold flex items-center gap-1">
                                                <FaClock size={10} /> {new Date(n.createdAt).toLocaleDateString()}
                                            </span>
                                            {n.status === 'unread' && (
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-200"></span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="p-2 bg-white rounded-lg shadow-sm border border-slate-100 text-slate-400 hover:text-emerald-600 transition-colors">
                                        <FaCheckCircle size={14} />
                                    </div>
                                </div>
                            </li>
                         )) : (
                            <div className="p-16 text-center">
                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-200">
                                    <FaBell size={24} />
                                </div>
                                <h4 className="text-sm font-black text-slate-800 uppercase tracking-widest">All Caught Up!</h4>
                                <p className="text-xs text-slate-400 mt-1 font-medium">You have no new notifications.</p>
                            </div>
                        )}
                    </ul>

                    <div className="p-4 bg-slate-50/50 border-t border-slate-50 text-center">
                        <button 
                            onClick={() => { navigate('/notifications'); setIsOpen(false); }}
                            className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] hover:text-slate-600 transition-colors"
                        >
                            View All History
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsDropdown;
